import { defineComponent as _defineComponent } from 'vue'
import bugsnagClient from 'JS/global/bugsnag';
import Icon from 'JS/components/Icon.vue';
import LinkButton from 'JS/components/LinkButton.vue';
import SavedVehicle from 'JS/components/automotive/SavedVehicle.vue';
import SavedVehicleMetadata from 'JS/components/automotive/SavedVehicleMetadata.vue';
import Spinner from 'JS/components/Spinner.vue';
import StepsProgress from 'JS/components/automotive/vehicle-lookup/steps/StepsProgress.vue';
import StepWrapper from 'JS/components/automotive/vehicle-lookup/steps/StepWrapper.vue';
import TireProductCard from './TireProductCard.vue';
import TireRecommendationQuestion from './TireRecommendationQuestion.vue';
import TireRecommendationService from '../services/TireRecommendationService';
import UiButton from 'JS/components/UiButton.vue';
import { computed, defineComponent, nextTick, onBeforeMount, PropType, ref } from 'vue';
import { getTireQuestionsForVehicle } from  '../data/questions';
import { getVehicleTireListLink } from 'JS/automotive/shared/utils/links';
import { ICustomerVehicle } from 'JS/types/Automotive';
import { IListableProductJson } from 'JS/types/Product';
import { ITireRecommendationAnswer, ITireRecommendationQuestion } from '../types/TireRecommendation.types';
import { objectToQueryString } from 'JS/utilities/url';
import { useBreakpoints } from 'JS/composables/breakpoint';


export default /*#__PURE__*/_defineComponent({
  __name: 'TireRecommender',
  props: {
	vehicle: {
		type: Object as PropType<ICustomerVehicle>,
		required: true,
	}
},
  emits: ["recommendationsLoaded"],
  setup(__props, { emit }: { emit: ({
	(event: 'recommendationsLoaded', recommendations: IListableProductJson[]): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props

const recommendationService = new TireRecommendationService();





const recommendationsHeading = ref<HTMLHeadingElement | null>(null);

const currentStep = ref<number>(1);
const loading = ref<boolean>(true);
const noTiresAvailable = ref<boolean>(false);
const recommendations = ref<IListableProductJson[] | null>(null);
const warrantyCodesMap = ref<Record<number, string>>({});
const questions = ref<ITireRecommendationQuestion[]>([]);
const selectedAnswersMap = ref<Record<string, ITireRecommendationAnswer[]>>({});
const { isMobile } = useBreakpoints();

const activeQuestion = computed<ITireRecommendationQuestion | null>(() => {
	return questions.value[currentStep.value - 1] || null;
});

const allTiresLink = computed<string>(() => {
	return getVehicleTireListLink(props.vehicle, {
		recs: recommendations.value?.map(x => x.blainNumber)?.join(','),
	});
});

const isOnLastQuestion = computed<boolean>(() => {
	return currentStep.value >= questions.value.length;
});

const numberOfSteps = computed<number>(() => {
	return questions.value.length + 1;
});

const minWarrantyMiles = computed<number>( () => {
	const miles = selectedAnswers.value
		.reduce((currentMiles, { warrantyFactor }) => currentMiles * warrantyFactor, 1);

	return Math.min(90000, miles);
});

const warrantyCodes = computed<string[]>( () => {
	return Object.entries(warrantyCodesMap.value)
		.filter(([miles]) => parseInt(miles) >= minWarrantyMiles.value)
		.map(([_, code]) => code);
});

const selectedAnswers = computed((): ITireRecommendationAnswer[] => {
	return Object.values(selectedAnswersMap.value).flat();
});

const noRecommendations = computed(() => {
	return noTiresAvailable.value || (currentStep.value > questions.value.length && !recommendations.value?.length);
});

const vehicleLayout = computed(() => {
	return isMobile.value ? 'hybrid' : 'horizontal';
});

const compareUrl = computed((): string | null => {
	if (!recommendations.value?.length || recommendations.value.length <= 1) return null;

	const skus = recommendations.value.map(r => r.blainNumber).join(',');

	const returnUrl = new URL(
		window.location.pathname.startsWith('/tires/list')
			? window.location.href
			: allTiresLink.value,
		window.location.origin
	);

	returnUrl.searchParams.set('compare', skus);

	return `/tires/compare?${objectToQueryString({
		skus,
		returnUrl: returnUrl.toString(),
	})}`;
});

function questionHasBeenAnswered(question: ITireRecommendationQuestion): boolean {
	return !!selectedAnswersMap.value[question.id]?.length;
}


function sendGtmEvent(eventName: String) {
	window.blainGtmDataLayer.push({
		'automotiveEvent': eventName,
		'event': "automotive_event"
	});
}

async function continueToNextStep() {
	sendGtmEvent(`tire_guide_step${currentStep.value}`);
	if (isOnLastQuestion.value) {
		await getRecommendations();
		currentStep.value++;
	} else {
		currentStep.value++;
	}
}

async function getRecommendations() {
	loading.value = true;
	try {
		recommendations.value = await recommendationService.getRecommendations({
			tireSizeDescription: props.vehicle.tireSizeDescription,
			idealWarrantyCodes: warrantyCodes.value.slice(0, 3),
			warrantyCodes: warrantyCodes.value.slice(3),
			offRoad: selectedAnswers.value.some(x => !!x.offRoad),
			towing: selectedAnswers.value.some(x => !!x.towing)
		});

		nextTick(() => {
			recommendationsHeading.value?.focus();
		});

		emit('recommendationsLoaded', recommendations.value);

		return recommendations.value;
	} catch (e) {
		bugsnagClient.notify(e);
	} finally {
		loading.value = false;
	}
}

function updateQuestionAnswers(questionId: string, answers: ITireRecommendationAnswer[]) {
	selectedAnswersMap.value[questionId] = answers;
}

function startOver() {
	questions.value = getTireQuestionsForVehicle(props.vehicle);
	selectedAnswersMap.value = questions.value.reduce((map, question) => ({
		...map,
		[question.id]: [],
	}), {});
	currentStep.value = 1;
	recommendations.value = null;
}

onBeforeMount(async () => {
	loading.value = true;
	try {
		const codesReq = recommendationService.getWarrantyCodes();
		// Ensure there are any tires available at all
		const recs = await recommendationService.getRecommendations({
			tireSizeDescription: props.vehicle.tireSizeDescription,
			idealWarrantyCodes: [],
			warrantyCodes: [],
			offRoad: false,
			towing: false
		});

		noTiresAvailable.value = !recs?.length;
		if (noTiresAvailable.value) return;

		warrantyCodesMap.value = await codesReq;
		startOver();
	} catch (e) {
		bugsnagClient.notify(e);
	} finally {
		loading.value = false;
	}
});

return { __sfc: true,recommendationService, props, emit, recommendationsHeading, currentStep, loading, noTiresAvailable, recommendations, warrantyCodesMap, questions, selectedAnswersMap, isMobile, activeQuestion, allTiresLink, isOnLastQuestion, numberOfSteps, minWarrantyMiles, warrantyCodes, selectedAnswers, noRecommendations, vehicleLayout, compareUrl, questionHasBeenAnswered, sendGtmEvent, continueToNextStep, getRecommendations, updateQuestionAnswers, startOver, Icon, LinkButton, SavedVehicle, SavedVehicleMetadata, Spinner, StepsProgress, StepWrapper, TireProductCard, TireRecommendationQuestion, UiButton }
}

})